import {
    CpNotification,
    NotificationAction,
    NotificationLevel,
    NotificationReq
} from '../contracts/socket/notification-req';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NotificationComponent } from '../../partial-views/notication/notification.component';
import { BackendErrorContainerRes } from '../contracts/v0/cp';
import { L10nTranslationService } from 'angular-l10n';
import {Injectable, NgZone} from '@angular/core';
import { Moment } from 'moment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NotificationStoreService } from './notification-store.service';
import { RestApiService } from './rest-api.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationService  {
    private readonly messageArchiveSubject = new BehaviorSubject<CpNotification[]>([]);
    readonly messageArchive$ = this.messageArchiveSubject.asObservable();

    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    readonly unreadCount$ = this.unreadCountSubject.asObservable();

    messageArchive: CpNotification[] = [];
    maxNotifications = 100;

    config: MatSnackBarConfig = {
        verticalPosition: 'top',
        duration: 8000,
    };

    constructor(
        public snackbar: MatSnackBar,
        private locService: L10nTranslationService,
        private ngZone: NgZone,
        private notificationStore: NotificationStoreService,
        private restApiService: RestApiService
    ) {
        this.messageArchive = this.notificationStore.load();
        this.unreadCountSubject.next(this.calculateLocalUnreadCount());
        this.emitArchive(false);
    }

    get notifications(): CpNotification[] {
        return [...this.messageArchive];
    }

    get unreadCount(): number {
        return this.unreadCountSubject.value;
    }

    async syncFromServer(take: number = this.maxNotifications, unreadOnly = false): Promise<void> {
        try {
            const [items, unreadCount] = await Promise.all([
                firstValueFrom(this.restApiService.v0.notifications.search(take, unreadOnly)),
                firstValueFrom(this.restApiService.v0.notifications.unreadCount())
            ]);

            const mapped = (items ?? []).map(item => this.mapFromApi(item));
            this.mergeServerNotifications(mapped);

            this.unreadCountSubject.next(unreadCount ?? 0);
            this.emitArchive();
        } catch (error) {
            console.error('[NotificationService] syncFromServer failed', error);
        }
    }

    success(translationMsgKey: string) {
        const header = this.locService.translate('generalSuccess');
        const text = this.locService.translate(translationMsgKey);
        const not = this.createSnackbarNotification(header, [text], true, { level: 'success' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    successMessage(message: string, title?: string) {
        const header = title ?? this.locService.translate('generalSuccess');
        const not = this.createSnackbarNotification(header, [message], true, { level: 'success' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    error(translationMsgKey: string) {
        const header = this.locService.translate('generalError');
        const text = this.locService.translate(translationMsgKey);
        const not = this.createSnackbarNotification(header, [text], false, { level: 'error' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    errorMessage(message: string, title?: string) {
        const header = title ?? this.locService.translate('generalError');
        const not = this.createSnackbarNotification(header, [message], false, { level: 'error' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    info(message: string, title?: string) {
        const not = this.createSnackbarNotification(title ?? '', [message], true, { level: 'info' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    warning(message: string, title?: string) {
        const not = this.createSnackbarNotification(title ?? '', [message], false, { level: 'warning' });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    /** Error snackbar with several messages and an optional action (e.g. a link to fix the problem). */
    errorWithAction(messages: string[], action?: NotificationAction, title?: string, durationMs?: number) {
        const header = title ?? this.locService.translate('generalError');
        const not = this.createSnackbarNotification(header, messages, false, { level: 'error', action, durationMs });

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    errorFromBackendError(error: BackendErrorContainerRes) {
        const header = this.locService.translate('generalError');
        let not: MatSnackBarConfig;

        if ((error?.data?.errors?.length ?? 0) > 0) {
            const errors: string[] = [];

            error.data!.errors.forEach(value => {
                const fieldName = value.fieldName?.split('.').pop();
                const translatedField = fieldName ? this.locService.translate(fieldName) : '';
                const hasReadableField = !!fieldName && !!translatedField && translatedField !== fieldName;
                errors.push(hasReadableField ? `${translatedField}: ${value.message}` : value.message);
            });

            not = this.createSnackbarNotification(header, errors, false, { level: 'error' });
        } else {
            not = this.createSnackbarNotification(
                header + ': ' + error.exception?.errorCode,
                [error.exception?.message ?? ''],
                false,
                { level: 'error' }
            );
        }

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, not);
        });
    }

    show(notification: NotificationReq) {
        const mapped = this.mapFromApi(notification);
        const existed = this.upsertNotification(mapped);

        if (!mapped.read && !existed) {
            this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
        }

        this.emitArchive();

        this.ngZone.run(() => {
            this.snackbar.openFromComponent(NotificationComponent, {
                ...this.config,
                duration: notification.durationMs ?? 40000,
                data: mapped,
                panelClass: [
                    'cp-notification-snackbar',
                    `cp-notification-snackbar-${mapped.level ?? 'info'}`
                ]
            });
        });
    }

    markAsRead(notification: CpNotification) {
        if (notification.read) {
            return;
        }

        notification.read = true;
        this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
        this.emitArchive();

        if (notification.id) {
            void this.markAsReadRemoteAsync(notification.id);
        }
    }

    markAllAsRead() {
        const changed = this.messageArchive.some(x => !x.read);
        if (!changed) {
            return;
        }

        this.messageArchive.forEach(x => x.read = true);
        this.unreadCountSubject.next(0);
        this.emitArchive();

        void this.markAllAsReadRemoteAsync();
    }

    clearAll() {
        this.messageArchive = [];
        this.emitArchive();
        void this.clearAllRemoteAsync();
    }

    closeAll() {
        this.ngZone.run(() => {
            this.snackbar.dismiss();
        });
    }

    private createSnackbarNotification(
        title: string,
        messages: string[],
        success: boolean,
        options?: {
            id?: number;
            read?: boolean;
            createdAtUtc?: string | Date | Moment;
            level?: NotificationLevel;
            action?: NotificationAction;
            details?: Record<string, any>;
            source?: string;
            category?: string;
            durationMs?: number;
        }
    ): MatSnackBarConfig {
        const level = options?.level ?? (success ? 'success' : 'error');

        const notification = new CpNotification(title, messages, success, {
            id: options?.id,
            read: options?.read,
            createdAtUtc: options?.createdAtUtc,
            level,
            action: options?.action,
            details: options?.details,
            source: options?.source,
            category: options?.category,
        });


        return {
            ...this.config,
            duration: options?.durationMs ?? this.config.duration,
            data: notification,
            panelClass: [
                'cp-notification-snackbar',
                `cp-notification-snackbar-${level}`
            ]
        };
    }

    private emitArchive(persist = true) {
        this.messageArchive = this.messageArchive
            .sort((a, b) => b.createdAtUtc.valueOf() - a.createdAtUtc.valueOf())
            .slice(0, this.maxNotifications);

        if (persist) {
            this.notificationStore.save(this.messageArchive);
        }

        this.messageArchiveSubject.next([...this.messageArchive]);
    }

    private resolveMessages(notification: NotificationReq): string[] {
        if (notification.messages?.length) {
            return notification.messages.filter(x => !!x);
        }

        if (notification.text) {
            return [notification.text];
        }

        return [];
    }

    private resolveLevel(notification: NotificationReq): NotificationLevel {
        if (notification.level) {
            return notification.level;
        }

        if (notification.success === true) {
            return 'success';
        }

        if (notification.success === false) {
            return 'error';
        }

        return 'info';
    }

    private resolveSuccess(notification: NotificationReq, level: NotificationLevel): boolean {
        if (typeof notification.success === 'boolean') {
            return notification.success;
        }

        return level === 'success' || level === 'info';
    }

    private getDefaultTitle(level: NotificationLevel): string {
        switch (level) {
            case 'success':
                return this.locService.translate('generalSuccess');
            case 'error':
                return this.locService.translate('generalError');
            case 'warning':
                return this.locService.translate('generalWarning');
            default:
                return this.locService.translate('generalInfo');
        }
    }

    private calculateLocalUnreadCount(): number {
        return this.messageArchive.filter(x => !x.read).length;
    }

    private mapFromApi(notification: NotificationReq): CpNotification {
        const level = this.resolveLevel(notification);
        const success = this.resolveSuccess(notification, level);
        const title = notification.title ?? this.getDefaultTitle(level);
        const messages = this.resolveMessages(notification);

        return new CpNotification(title, messages, success, {
            id: notification.id,
            read: notification.read,
            createdAtUtc: notification.createdAtUtc,
            level,
            action: notification.action,
            details: notification.details,
            source: notification.source,
            category: notification.category
        });
    }

    private upsertNotification(notification: CpNotification): boolean {
        if (notification.id) {
            const index = this.messageArchive.findIndex(x => x.id === notification.id);

            if (index >= 0) {
                this.messageArchive[index] = notification;
                return true;
            }
        }

        this.messageArchive.splice(0, 0, notification);

        if (this.messageArchive.length > this.maxNotifications) {
            this.messageArchive = this.messageArchive.slice(0, this.maxNotifications);
        }

        return false;
    }

    private mergeServerNotifications(serverNotifications: CpNotification[]) {
        const byId = new Map<number, CpNotification>();

        for (const existing of this.messageArchive) {
            if (existing.id) {
                byId.set(existing.id, existing);
            }
        }

        for (const incoming of serverNotifications) {
            if (incoming.id) {
                byId.set(incoming.id, incoming);
            }
        }

        this.messageArchive = [
            ...Array.from(byId.values())
        ]
            .sort((a, b) => b.createdAtUtc.valueOf() - a.createdAtUtc.valueOf())
            .slice(0, this.maxNotifications);
    }

    private async markAsReadRemoteAsync(notificationId: number): Promise<void> {
        try {
            await firstValueFrom(this.restApiService.v0.notifications.read(notificationId));
        } catch (error) {
            console.error('[NotificationService] markAsReadRemoteAsync failed', error);
            await this.syncFromServer();
        }
    }

    private async markAllAsReadRemoteAsync(): Promise<void> {
        try {
            await firstValueFrom(this.restApiService.v0.notifications.readAll());
        } catch (error) {
            console.error('[NotificationService] markAllAsReadRemoteAsync failed', error);
            await this.syncFromServer();
        }
    }

    private async clearAllRemoteAsync(): Promise<void> {
        try {
            await firstValueFrom(this.restApiService.v0.notifications.clearAll());
        } catch (error) {
            console.error('[NotificationService] clearAllRemoteAsync failed', error);
            await this.syncFromServer();
        }
    }

}